import React, { useState } from "react";
import { Box, Container, Heading, Text, VStack, FormControl, FormLabel, Input, Button, SimpleGrid, Table, Thead, Tbody, Tr, Th, Td, Badge } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FinanceToolsComparison: React.FC = () => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [loanTerm, setLoanTerm] = useState(30);

  const loanOptions = [
    { name: "Basic Variable", rate: 5.99, offset: false, redraw: true, fees: 0 },
    { name: "Premium Variable", rate: 6.15, offset: true, redraw: true, fees: 395 },
    { name: "Fixed 3 Year", rate: 6.29, offset: false, redraw: false, fees: 0 },
    { name: "Fixed 5 Year", rate: 6.45, offset: false, redraw: false, fees: 0 },
  ];

  const calculateRepayment = (principal: number, rate: number, years: number) => {
    const monthlyRate = rate / 100 / 12;
    const months = years * 12;
    return Math.round((principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months)));
  };

  return (
    <Box minH="100vh" bg="white">
      <SEOHead pagePath="/finance/tools/comparison" fallbackTitle="Loan Comparison Tool" fallbackDescription="Compare different loan options and rates" />
      <NavbarWithCallToAction />
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">Finance Section</Text>
            <Heading size="2xl" fontWeight="bold">Loan Comparison Calculator</Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Compare different loan options</Text>
          </VStack>
        </Container>
      </Box>
      <Container maxW="container.xl" py={20} flex="1">
        <VStack spacing={10}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full">
            <FormControl>
              <FormLabel>Loan Amount</FormLabel>
              <Input type="number" value={loanAmount} onChange={(e) => setLoanAmount(Number(e.target.value))} size="lg" />
            </FormControl>
            <FormControl>
              <FormLabel>Loan Term (years)</FormLabel>
              <Input type="number" value={loanTerm} onChange={(e) => setLoanTerm(Number(e.target.value))} size="lg" />
            </FormControl>
          </SimpleGrid>
          <Box w="full" overflowX="auto">
            <Table variant="simple" size="md">
              <Thead bg="gray.100">
                <Tr>
                  <Th>Loan Product</Th>
                  <Th>Interest Rate</Th>
                  <Th>Monthly Repayment</Th>
                  <Th>Total Interest</Th>
                  <Th>Features</Th>
                  <Th>Annual Fee</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loanOptions.map((loan, index) => {
                  const repayment = calculateRepayment(loanAmount, loan.rate, loanTerm);
                  const totalInterest = (repayment * loanTerm * 12) - loanAmount;
                  return (
                    <Tr key={index} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight={600}>{loan.name}</Td>
                      <Td><Badge colorScheme={loan.rate < 6.1 ? "green" : loan.rate < 6.3 ? "blue" : "orange"}>{loan.rate}%</Badge></Td>
                      <Td fontWeight={600}>${repayment.toLocaleString()}</Td>
                      <Td>${Math.round(totalInterest).toLocaleString()}</Td>
                      <Td fontSize="sm">
                        {loan.offset && "✓ Offset "}
                        {loan.redraw && "✓ Redraw"}
                        {!loan.offset && !loan.redraw && "None"}
                      </Td>
                      <Td>${loan.fees}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full">
            <Box bg="blue.50" p={6} borderRadius="lg">
              <Heading size="sm" mb={2} color="blue.800">Lowest Rate</Heading>
              <Text fontSize="2xl" fontWeight={700} color="blue.600">5.99%</Text>
              <Text fontSize="sm" color="gray.600">Basic Variable</Text>
            </Box>
            <Box bg="green.50" p={6} borderRadius="lg">
              <Heading size="sm" mb={2} color="green.800">Lowest Repayment</Heading>
              <Text fontSize="2xl" fontWeight={700} color="green.600">${calculateRepayment(loanAmount, 5.99, loanTerm).toLocaleString()}</Text>
              <Text fontSize="sm" color="gray.600">Per month</Text>
            </Box>
            <Box bg="purple.50" p={6} borderRadius="lg">
              <Heading size="sm" mb={2} color="purple.800">Best Value</Heading>
              <Text fontSize="2xl" fontWeight={700} color="purple.600">Premium</Text>
              <Text fontSize="sm" color="gray.600">Offset + Redraw</Text>
            </Box>
          </SimpleGrid>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceToolsComparison;
